/**
 * Compact show-mode control overlaid on On Air / Next preview frames (bottom-right).
 * On Air → immediate playout; Next → look-ahead (applied on cue advance).
 */

import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, SvgIcon, Tooltip } from '@mui/material';
import { PersonOutlined, PictureInPictureOutlined } from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import type { IconType } from 'react-icons';
import { GoFileMedia } from 'react-icons/go';
import { LiveContextShowMode } from '@/enums/live-context-show-mode.enum';
import { useUpdateLiveContextMutation } from '@/state/mutations/player/use-update-live-context';
import { useUpdateStagedSelectionMutation } from '@/state/mutations/player/use-update-staged-selection';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useTranslations } from '@/i18n/use-translations';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import type { FlowControlPerformMode } from '@/types/player/simulation-cue-sheet.types';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import {
  liveNextItemIdFromHoldKey,
  useFlowControlLiveNextShowModeStore,
} from '@/state/stores/use-flow-control-live-next-show-mode-store';
import { useLiveContextStateWithLivePlayState } from '@/hooks/use-live-play-state';

export type PerformShowModeOverlayTarget = 'onAir' | 'next';

export interface PerformShowModeOverlayProps {
  flow: FlowDto;
  entityRef: string;
  target: PerformShowModeOverlayTarget;
  performMode?: FlowControlPerformMode;
  liveContextState?: LiveContextStateDto;
  /** Simulation: hold preselect re-apply after operator overlay edits. */
  nextCueIdentity?: string;
  /** LIVE: projected look-ahead show mode from cueState (preferred over stagedSelection). */
  lookAheadShowMode?: 'mediaOnly' | 'both' | 'speakerOnly';
  /** LIVE: Next item id to latch so preload ticks do not clobber the override. */
  nextItemId?: string;
}

const SHOW_MODE_OPTIONS: Array<{
  mode: LiveContextShowMode;
  titleKey:
    | 'flows.detail.perform.lowerControls.showMode.mediaOnly'
    | 'flows.detail.perform.lowerControls.showMode.mixedView'
    | 'flows.detail.perform.lowerControls.showMode.speakerOnly';
  Icon: SvgIconComponent | IconType;
}> = [
  {
    mode: LiveContextShowMode.MEDIA_ONLY,
    titleKey: 'flows.detail.perform.lowerControls.showMode.mediaOnly',
    Icon: GoFileMedia,
  },
  {
    mode: LiveContextShowMode.BOTH,
    titleKey: 'flows.detail.perform.lowerControls.showMode.mixedView',
    Icon: PictureInPictureOutlined,
  },
  {
    mode: LiveContextShowMode.SPEAKER_ONLY,
    titleKey: 'flows.detail.perform.lowerControls.showMode.speakerOnly',
    Icon: PersonOutlined,
  },
];

function ShowModeOptionIcon({ Icon, fontSize }: { Icon: SvgIconComponent | IconType; fontSize: number }) {
  if ('muiName' in Icon) {
    return <Icon sx={{ fontSize }} />;
  }
  return <SvgIcon component={Icon} inheritViewBox sx={{ fontSize }} />;
}

function toLiveContextShowMode(showMode: string): LiveContextShowMode {
  return showMode === 'both' || showMode === 'mixedView' ? LiveContextShowMode.BOTH : (showMode as LiveContextShowMode);
}

function toSimShowMode(mode: LiveContextShowMode): 'mediaOnly' | 'both' | 'speakerOnly' {
  if (mode === LiveContextShowMode.BOTH) {
    return 'both';
  }
  if (mode === LiveContextShowMode.SPEAKER_ONLY) {
    return 'speakerOnly';
  }
  return 'mediaOnly';
}

export function PerformShowModeOverlay({
  flow,
  entityRef,
  target,
  performMode = 'play',
  liveContextState,
  nextCueIdentity,
  lookAheadShowMode,
  nextItemId,
}: PerformShowModeOverlayProps) {
  const t = useTranslations();
  const setFlow = useFlowStore((s) => s.setFlow);
  const simulationOnAirShowMode = useFlowControlSimulationCueStore((s) => s.onAirShowMode);
  const simulationNextShowMode = useFlowControlSimulationCueStore((s) => s.nextShowMode);
  const setOnAirShowMode = useFlowControlSimulationCueStore((s) => s.setOnAirShowMode);
  const setNextShowMode = useFlowControlSimulationCueStore((s) => s.setNextShowMode);
  const holdNextShowModePreselect = useFlowControlSimulationCueStore((s) => s.holdNextShowModePreselect);
  const holdLiveNextShowMode = useFlowControlLiveNextShowModeStore((s) => s.hold);
  const heldLiveNextShowMode = useFlowControlLiveNextShowModeStore((s) => s.showMode);
  const { mutateAsync: updateLiveContextMutation, isPending: isOnAirMutating } = useUpdateLiveContextMutation();
  const updateStagedSelection = useUpdateStagedSelectionMutation();
  const [pendingMode, setPendingMode] = useState<LiveContextShowMode | null>(null);
  const lastRequestedOnAirShowMode = useRef<LiveContextShowMode | null>(null);

  const isSimulation = performMode === 'simulation';
  /** Same WS merge as Current preview — live GET is skipped, so GET playState.showMode stays the cue. */
  const livePlayStateContext = useLiveContextStateWithLivePlayState(
    performMode === 'play' ? entityRef : undefined,
    liveContextState,
  );
  /** Item id only — never `item|slot|scope`, or syncNextItemId treats first click as a cue advance. */
  const resolvedNextItemId =
    nextItemId ??
    livePlayStateContext?.cueState?.nextItemId ??
    liveContextState?.cueState?.nextItemId ??
    liveNextItemIdFromHoldKey(nextCueIdentity);
  const onAirItemKey = (
    livePlayStateContext?.playState?.playedItemIds ??
    liveContextState?.playState?.playedItemIds ??
    []
  )
    .map((entry) => entry.itemId)
    .filter(Boolean)
    .join(',');

  useEffect(() => {
    lastRequestedOnAirShowMode.current = null;
  }, [entityRef, performMode, onAirItemKey]);

  const currentShowMode = (() => {
    if (isSimulation) {
      const sim = target === 'onAir' ? simulationOnAirShowMode : simulationNextShowMode;
      return toLiveContextShowMode(sim);
    }
    if (target === 'onAir') {
      // Before the first take there is no play state. The show starts in the stored On Air mode.
      const playMode =
        livePlayStateContext?.playState?.showMode ??
        liveContextState?.playState?.showMode ??
        flow?.liveContext?.showMode;
      return playMode != null ? toLiveContextShowMode(playMode) : LiveContextShowMode.MEDIA_ONLY;
    }
    // Operator hold always wins until Next advances (cleared in syncNextItemId).
    // Do not require hold-key equality — key can churn between itemId and cueIdentity.
    if (heldLiveNextShowMode != null) {
      return toLiveContextShowMode(heldLiveNextShowMode);
    }
    const nextMode = lookAheadShowMode ?? liveContextState?.stagedSelection?.showMode;
    if (nextMode != null) {
      return toLiveContextShowMode(nextMode);
    }
    return LiveContextShowMode.MEDIA_ONLY;
  })();

  // Next uses local hold for immediate UI — do not block clicks on staged-selection in-flight.
  const isPending = pendingMode != null || (target === 'onAir' && isOnAirMutating);

  const handleModeChange = async (newMode: LiveContextShowMode) => {
    if (isPending) {
      return;
    }
    // Next: skip a no-op against the painted overlay.
    // On Air: WS can highlight a take/look-ahead mode the player has not applied — still PATCH.
    if (target !== 'onAir' && newMode === currentShowMode) {
      return;
    }
    if (target === 'onAir' && !isSimulation && lastRequestedOnAirShowMode.current === newMode) {
      return;
    }

    if (isSimulation) {
      const mapped = toSimShowMode(newMode);
      if (target === 'onAir') {
        setOnAirShowMode(mapped);
      } else {
        if (nextCueIdentity) {
          holdNextShowModePreselect(nextCueIdentity);
        }
        setNextShowMode(mapped);
      }
      return;
    }

    if (!entityRef) {
      return;
    }

    if (target === 'next') {
      const mapped = toSimShowMode(newMode);
      const lookAheadForItemId = resolvedNextItemId;
      // Local hold first so overlay/preview survive perform-control WS hydrates.
      if (lookAheadForItemId) {
        holdLiveNextShowMode(lookAheadForItemId, mapped);
      }
      // Look-ahead-only payload: do not send skippedItemIds/focusedItemId (stale [] was
      // treated as skip-clear → preload re-apply → showMode clobber on BE).
      updateStagedSelection.mutate({
        entityRef,
        stagedSelection: {
          selectedItemIds: [],
          showMode: mapped,
          lookAheadForItemId,
        },
      });
      return;
    }

    // On Air — immediate playout via PATCH live-context.
    if (!flow?.liveContext) {
      return;
    }
    const separator = entityRef.indexOf(':');
    if (separator < 0) {
      return;
    }
    const entityType = entityRef.slice(0, separator) as 'Flow' | 'Event';
    const entityId = entityRef.slice(separator + 1);
    if (!entityId) {
      return;
    }

    const original = flow.liveContext.showMode;
    lastRequestedOnAirShowMode.current = newMode;
    setPendingMode(newMode);
    setFlow({
      ...flow,
      liveContext: { ...flow.liveContext, showMode: newMode },
    });
    try {
      await updateLiveContextMutation({
        entityType,
        entityId,
        showMode: newMode,
      });
    } catch {
      lastRequestedOnAirShowMode.current = null;
      setFlow({
        ...flow,
        liveContext: { ...flow.liveContext, showMode: original },
      });
    } finally {
      setPendingMode(null);
    }
  };

  const activeColor = isSimulation ? 'info' : 'error';

  return (
    <Box
      sx={{
        position: 'absolute',
        right: 6,
        bottom: 6,
        zIndex: 3,
        display: 'flex',
        gap: 0.5,
        pointerEvents: 'auto',
        bgcolor: 'transparent',
      }}
    >
      {SHOW_MODE_OPTIONS.map(({ mode, titleKey, Icon }) => {
        const isActive = currentShowMode === mode;
        const isTargetPending = pendingMode === mode;
        return (
          <Tooltip key={mode} title={t(titleKey)}>
            <span>
              <IconButton
                onClick={() => void handleModeChange(mode)}
                color={isActive || isTargetPending ? activeColor : 'inherit'}
                size="medium"
                disabled={isPending}
                aria-busy={isTargetPending}
                sx={{
                  color: isActive || isTargetPending ? undefined : 'rgba(255,255,255,0.85)',
                  p: 0.75,
                }}
              >
                {isTargetPending ? (
                  <CircularProgress size={28} color="inherit" />
                ) : (
                  <ShowModeOptionIcon Icon={Icon} fontSize={28} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        );
      })}
    </Box>
  );
}
